import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Rocket,
  Sparkles,
  Globe,
  BookOpen,
  MessageCircle,
  Mail,
  Loader2,
  Trash2,
  Zap,
} from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function Wingman() {
  const [briefs, setBriefs] = useState([]);
  const [running, setRunning] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/wingman/briefs").then((r) => setBriefs(r.data.briefs)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      await api.post("/wingman/run");
      toast.success("Wingman is researching your upcoming meetings — briefs will appear shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wingman run failed");
      setRunning(false);
      return;
    }
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      await load();
      if (tries >= 12) { clearInterval(iv); setRunning(false); }
    }, 4000);
  };

  const remove = async (id) => {
    try { await api.delete(`/wingman/briefs/${id}`); load(); toast.success("Removed"); } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F6F7F9]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <Rocket className="h-6 w-6 text-[#193962]" />
              <h1 className="font-[Outfit] font-bold tracking-tight text-4xl sm:text-5xl">AI Wingman</h1>
            </div>
            <p className="text-base text-muted-foreground font-[Manrope] mt-2">
              Proactive pre-meeting intelligence. Runs hourly to prep you for new-client meetings — wysyła pełny brief i przypomnienie 1h przed na e‑mail (WhatsApp wyłączony).
            </p>
          </div>
          <Button onClick={run} disabled={running} data-testid="wingman-run-btn" className="h-11 px-5 bg-[#193962] hover:bg-[#12294A] text-white rounded-md transition-all duration-200 hover:-translate-y-[1px]">
            {running ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Researching…</>) : (<><Zap className="h-4 w-4 mr-2" /> Run Wingman Now</>)}
          </Button>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wingman-briefs">
          {briefs.length === 0 ? (
            <div className="col-span-full border border-border bg-white rounded-md py-16 text-center">
              <Rocket className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
              <p className="font-[Manrope] text-muted-foreground">No briefs yet. Click "Run Wingman Now" to prep for your upcoming client meetings.</p>
            </div>
          ) : briefs.map((b, i) => (
            <motion.div key={b.brief_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="border border-border bg-white rounded-md p-5 flex flex-col" data-testid={`brief-card-${b.brief_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-[Outfit] font-semibold truncate">{b.client_name}</p>
                  <p className="text-xs text-muted-foreground font-[Manrope]">{b.industry} · {b.region}</p>
                  <p className="text-xs text-[#193962] font-[Manrope] mt-0.5">Meeting: {fmtDateTime(b.meeting_start || b.meeting_date)}</p>
                </div>
                <button onClick={() => remove(b.brief_id)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50 text-zinc-400 hover:text-red-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
              <p className="text-sm font-[Manrope] text-zinc-600 mt-3 line-clamp-3 flex-1">{b.market_overview}</p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-[Manrope] ${b.email_status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}><Mail className="h-3 w-3" /> {b.email_status === "sent" ? "Brief emailed · 24h" : (b.stage_email ? "Email retry pending" : "Email pending")}</span>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-[Manrope] ${b.stage_nudge ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}><MessageCircle className="h-3 w-3" /> {b.stage_nudge ? "Nudge sent · 1h" : "Nudge scheduled · 1h"}</span>
              </div>
              <Button variant="outline" onClick={() => setViewing(b)} data-testid={`view-brief-${b.brief_id}`} className="mt-4">View Brief</Button>
            </motion.div>
          ))}
        </div>
      </main>

      <BriefDialog brief={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function BriefDialog({ brief, onClose }) {
  if (!brief) return null;
  return (
    <Dialog open={!!brief} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="wingman-brief-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl tracking-tight">Pre-Meeting Intelligence Brief</DialogTitle>
          <DialogDescription className="font-[Manrope]">{brief.client_name} · {brief.industry} · {brief.region}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Wingman nudge message (e-mail) */}
          <div className="border border-[#193962]/20 bg-[#193962]/5 rounded-md p-4" data-testid="wingman-email-nudge">
            <div className="flex items-center gap-2 mb-2"><MessageCircle className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">Przypomnienie e‑mail (1h przed)</h3></div>
            <p className="font-[Manrope] text-sm whitespace-pre-wrap text-zinc-700">{brief.wingman_message}</p>
          </div>

          <section data-testid="brief-market">
            <div className="flex items-center gap-2 mb-2"><Globe className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">Market Overview — Poland &amp; Kenya</h3></div>
            <p className="font-[Manrope] text-sm leading-relaxed text-zinc-700">{brief.market_overview || "—"}</p>
          </section>

          <section data-testid="brief-icebreakers">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">Icebreakers &amp; Fun Facts</h3></div>
            <ul className="space-y-1.5">{(brief.icebreakers || []).map((x, i) => (
              <li key={i} className="flex gap-2.5 font-[Manrope] text-sm text-zinc-700"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#193962] shrink-0" />{x}</li>
            ))}</ul>
          </section>

          <section data-testid="brief-jargon">
            <div className="flex items-center gap-2 mb-2"><BookOpen className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">Jargon Cheat Sheet</h3></div>
            <div className="space-y-2">{(brief.jargon || []).map((j, i) => (
              <div key={i} className="border border-border rounded-md px-3 py-2">
                <p className="font-[Manrope] font-semibold text-sm text-[#193962]">{j.term}</p>
                <p className="font-[Manrope] text-sm text-zinc-700">{j.definition}</p>
              </div>
            ))}</div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
